import { useState } from "react";
import { Alert, Button, Form, Spinner, Tab, Tabs } from "react-bootstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { BOT_API_URL } from './config.js';
import { saveAccount } from './api.js';
import DisboxFileManager from './disbox-file-manager';
import NavigationBar from './NavigationBar';

// Menyimpan akun (username, userId, webhook, dan token login dari bot) ke
// localStorage. Token wajib ada untuk semua request ke bot.
function storeAccount(account) {
    saveAccount({
        username: account.username,
        userId: account.userId,
        webhookUrls: account.webhookUrls,
        uploadWebhooks: account.uploadWebhooks,
        token: account.token,
        tokenExpiresAt: account.tokenExpiresAt,
    });
    // Bersihkan sisa alur setup lama (tempel webhook manual) supaya tidak bentrok.
    localStorage.removeItem("webhookUrl");
}

async function callAuthApi(path, body) {
    let response;
    try {
        response = await fetch(`${BOT_API_URL}${path}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    } catch (e) {
        throw new Error("Tidak bisa menghubungi bot Disbox. Pastikan bot sedang berjalan (lihat folder discord-bot).");
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || `Gagal (status ${response.status})`);
    }
    return data;
}

function Setup() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [tab, setTab] = useState("login");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const notice = params.has("relogin")
        ? "Demi keamanan, Disbox sekarang memakai sesi login. Silakan login sekali lagi di perangkat ini (file kamu tidak berubah)."
        : params.has("expired")
            ? "Sesi login sudah habis atau tidak berlaku lagi. Silakan login lagi."
            : null;

    const onSwitchTab = (key) => {
        setTab(key);
        setError(null);
    };

    const onLogin = async (event) => {
        event.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const account = await callAuthApi("/auth/login", { username: username.trim(), password });
            // Token disimpan DULU, karena sinkronisasi di bawah butuh token untuk bicara dengan bot.
            storeAccount(account);
            const manager = await DisboxFileManager.create(account.webhookUrls);
            // Selalu cocokkan data di perangkat ini dengan backup TERBARU di
            // Discord: perubahan dari perangkat lain ditarik, dan perubahan
            // di perangkat ini yang belum tersimpan digabungkan (tidak hilang).
            const { applied, error: syncError } = await manager.syncFromDiscordIfNewer();
            console.log(`[Disbox] Login sebagai "${account.username}", userId: ${manager.userId}, data diperbarui dari Discord: ${applied}`);
            if (syncError) {
                console.warn("[Disbox] Gagal sinkronisasi otomatis saat login:", syncError);
            }
            navigate("/");
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const onSignup = async (event) => {
        event.preventDefault();
        setError(null);
        if (password !== confirmPassword) {
            setError("Konfirmasi password tidak cocok.");
            return;
        }
        if (password.length < 8) {
            setError("Password minimal 8 karakter.");
            return;
        }
        setLoading(true);
        try {
            const account = await callAuthApi("/auth/signup", { username: username.trim(), password });
            storeAccount(account);
            navigate("/");
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const fieldStyle = { width: "80%", margin: "auto", fontSize: "1.5rem" };

    return (<div>
        <NavigationBar />
        <div className="App App-header" style={{ color: "black" }}>
            <div style={{ backgroundColor: "#FCFCFC", width: "66%", minHeight: "85vh", paddingBottom: "2rem" }}>
                <h1 style={{ fontSize: "6rem" }} className='mt-3'>
                    <b>{tab === "login" ? "Login" : "Sign Up"}</b>
                </h1>
                <h1 style={{ fontSize: "1.6rem" }}>
                    <b>{tab === "login" ? "Masuk ke akun Disbox kamu." : "Buat akun Disbox baru dalam beberapa detik."}</b>
                </h1>
                {notice && <Alert variant="info" style={{ width: "80%", margin: "1rem auto 0" }}>{notice}</Alert>}

                <Tabs activeKey={tab} onSelect={onSwitchTab} className="mt-4 justify-content-center"
                    style={{ width: "80%", margin: "2rem auto 0" }}>
                    <Tab eventKey="login" title="Login">
                        <Form onSubmit={onLogin} style={{ marginTop: "2rem" }}>
                            <Form.Group className="mb-3">
                                <Form.Control type="text" placeholder="Username" value={username}
                                    onChange={(e) => setUsername(e.target.value)} style={fieldStyle} required autoFocus />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control type="password" placeholder="Password" value={password}
                                    onChange={(e) => setPassword(e.target.value)} style={fieldStyle} required />
                            </Form.Group>
                            {error && <Alert variant="danger" style={{ width: "80%", margin: "1rem auto" }}>{error}</Alert>}
                            <Button type="submit" variant="primary" disabled={loading} style={{ ...fieldStyle, marginTop: "1rem" }}>
                                {loading ? <Spinner animation="border" size="sm" /> : <b>Login</b>}
                            </Button>
                        </Form>
                    </Tab>
                    <Tab eventKey="signup" title="Sign Up">
                        <Form onSubmit={onSignup} style={{ marginTop: "2rem" }}>
                            <Form.Group className="mb-3">
                                <Form.Control type="text" placeholder="Username (3-32 karakter)" value={username}
                                    onChange={(e) => setUsername(e.target.value)} style={fieldStyle} required
                                    minLength={3} maxLength={32} autoFocus />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control type="password" placeholder="Password (minimal 8 karakter)" value={password}
                                    onChange={(e) => setPassword(e.target.value)} style={fieldStyle} required minLength={8} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Control type="password" placeholder="Konfirmasi Password" value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)} style={fieldStyle} required />
                            </Form.Group>
                            {error && <Alert variant="danger" style={{ width: "80%", margin: "1rem auto" }}>{error}</Alert>}
                            <Button type="submit" variant="primary" disabled={loading} style={{ ...fieldStyle, marginTop: "1rem" }}>
                                {loading ? <Spinner animation="border" size="sm" /> : <b>Sign Up</b>}
                            </Button>
                        </Form>
                    </Tab>
                </Tabs>

                <div style={{ fontSize: "1rem", color: "#777", width: "80%", margin: "2rem auto 0", textAlign: "center" }}>
                    Saat Sign Up, bot Disbox otomatis membuatkan webhook penyimpanan file untukmu dan
                    mengingatnya di server Discord milik proyek ini, jadi tidak perlu copy-paste URL
                    webhook. Pendaftaran akun baru biasanya ditutup; admin bisa membukanya sementara
                    lewat perintah /disbox-signup di Discord. Pastikan bot Disbox sedang berjalan.
                </div>
            </div>
        </div>
    </div>);
}

export default Setup;
